// A88 — System Status: shared vocabulary for the sidebar widget, the banner and the full page.
// One definition of what each level looks like, so the three can never disagree.
import { useEffect, useState } from 'react';
import { statusAPI } from '../../services/api';
import { IcoCheck, IcoMinus, IcoAlert, IcoXCircle, IcoWrench, IcoNoData } from '../common/Icons';

export const LEVEL = {
  operational: { label: 'Operational', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', Ico: IcoCheck, rank: 0 },
  unknown:     { label: 'No recent data', color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', Ico: IcoNoData, rank: 0 },
  degraded:    { label: 'Degraded', color: '#ca8a04', bg: '#fefce8', border: '#fde68a', Ico: IcoMinus, rank: 1 },
  maintenance: { label: 'Maintenance', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', Ico: IcoWrench, rank: 1 },
  partial:     { label: 'Partial Outage', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', Ico: IcoAlert, rank: 2 },
  major:       { label: 'Major Outage', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', Ico: IcoXCircle, rank: 3 },
};
export const lv = (s) => LEVEL[s] || LEVEL.unknown;

// Plain words for a non-technical seller. "Partial Outage" means nothing to most people.
export const WORD = {
  operational: 'Working', unknown: 'Idle', degraded: 'Slow', maintenance: 'Maintenance',
  partial: 'Partly down', major: 'Down',
};
export const word = (s) => WORD[s] || WORD.unknown;

export const HEADLINE = {
  operational: 'All Systems Operational',
  unknown: 'All Systems Operational',
  degraded: 'Some Systems Are Slow',
  maintenance: 'Scheduled Maintenance',
  partial: 'Partial Outage',
  major: 'Major Outage',
};

// "Whose side is it on?" — the question this whole feature exists to answer.
export const OWNER = {
  modcommerce: { label: 'Our side', color: '#2563eb', bg: '#eff6ff', hint: 'A problem at ModCommerce. We are on it, nothing for you to change.' },
  courier:     { label: "Courier's side", color: '#c2410c', bg: '#fff7ed', hint: 'A problem at the courier, affecting every seller who uses them.' },
  you:         { label: 'Your setup', color: '#a16207', bg: '#fefce8', hint: 'Only your account is affected. The message says what to fix.' },
  channel:     { label: "Channel's side", color: '#7c3aed', bg: '#f5f3ff', hint: 'A problem at the sales channel, e.g. Shopify.' },
};

export const ago = (iso) => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)} days ago`;
};

// ── One poller for the whole tab ────────────────────────────────────────────
// The sidebar widget and the banner both read the summary. A module-level store means one request
// every few minutes per tab, not one per component, and it pauses while the tab is hidden.
const POLL_MS = 3 * 60 * 1000;
let summary = null;
const subs = new Set();
let timer = null;
let inflight = null;

const emit = () => subs.forEach(fn => fn(summary));
export const refreshSummary = () => {
  if (inflight) return inflight;
  inflight = statusAPI.summary()
    .then(r => { summary = r.data; emit(); })
    .catch(() => {})
    .finally(() => { inflight = null; });
  return inflight;
};
const onVisible = () => { if (document.visibilityState === 'visible') refreshSummary(); };
const start = () => {
  if (timer) return;
  refreshSummary();
  timer = setInterval(() => { if (document.visibilityState === 'visible') refreshSummary(); }, POLL_MS);
  document.addEventListener('visibilitychange', onVisible);
};
const stop = () => {
  clearInterval(timer); timer = null;
  document.removeEventListener('visibilitychange', onVisible);
};

export function useStatusSummary() {
  const [s, setS] = useState(summary);
  useEffect(() => {
    subs.add(setS);
    start();
    return () => { subs.delete(setS); if (!subs.size) stop(); };
  }, []);
  return s;
}
